"use client";

import { useEffect, useRef, type JSX } from "react";
import { AtomicLayoutSection, AtomicPager } from "@coveo/atomic-react";
import { ClientOnly } from "../../../atoms/ClientOnly";
import { type SearchPagerProps } from "./search-pager.props";

const PAGER_STYLES = `
  atomic-pager::part(page-button) {
    cursor: pointer;
  }
  atomic-pager::part(buttons) {
    float: right;
  }
  @media (min-width: 768px) {
    atomic-pager::part(buttons) {
      margin-right: 20px;
    }
  }
`;

// `<atomic-pager>` is a Coveo web component, so React's ref points at the
// custom element instance — not a typed React DOM node. The shadow DOM holds
// the radio inputs we want to label and bind click handlers on.
type PagerElement = HTMLAtomicPagerElement;

export const Default = (props: SearchPagerProps): JSX.Element => {
  console.log("---->[SearchPager:Default] Component", props.params);

  const { params } = props;
  const id = params?.RenderingIdentifier;
  const numberOfPages = Number.parseInt(
    params?.NumberOfPages ? params.NumberOfPages : "5",
    10,
  );
  const scrollTargetSelector = params?.ScrollTargetSelector
    ? params.ScrollTargetSelector
    : ".search-box";

  const pagerRef = useRef<PagerElement | null>(null);

  // Coveo re-renders the radio buttons inside its shadow root every time the
  // page changes, so we (1) observe the shadow tree and (2) re-apply both
  // aria-labels and click-to-scroll handlers whenever children mutate.
  const scrollToTarget = (scrollTargetSelector: string) => {
    const target = document.querySelector<HTMLElement>(scrollTargetSelector);

    if (target) {
      target.scrollIntoView({ block: "start" });
    } else {
      window.scrollTo({ top: 0 });
    }
  };

  const updatePagerLabels = (
    shadowRoot: ShadowRoot,
    scrollTargetSelector: string,
    cleanupHandlers: Array<() => void>,
  ) => {
    cleanupHandlers.splice(0).forEach((fn) => fn());

    const buttons = shadowRoot.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]',
    );

    buttons.forEach((btn) => {
      const page = btn.getAttribute("value");

      if (page) {
        btn.setAttribute("aria-label", `Page ${page}`);
      }

      const handleClick = () => scrollToTarget(scrollTargetSelector);

      btn.addEventListener("click", handleClick);

      cleanupHandlers.push(() => btn.removeEventListener("click", handleClick));
    });
  };

  useEffect(() => {
    const pagerEl = pagerRef.current;
    const shadowRoot = pagerEl?.shadowRoot;
    if (!shadowRoot) return;

    const cleanupHandlers: Array<() => void> = [];

    const observer = new MutationObserver(() =>
      updatePagerLabels(shadowRoot, scrollTargetSelector, cleanupHandlers),
    );

    observer.observe(shadowRoot, {
      childList: true,
      subtree: true,
    });

    updatePagerLabels(shadowRoot, scrollTargetSelector, cleanupHandlers);

    return () => {
      observer.disconnect();
      cleanupHandlers.forEach((fn) => fn());
    };
  }, [scrollTargetSelector]);

  return (
    <div
      className={`search-pager mb-(--global-component-spacing) px-(--spacing-sixteen) ${params?.styles ?? ""}`}
      data-role="search-pager-component"
    >
      <ClientOnly>
        <style>{PAGER_STYLES}</style>
        <AtomicLayoutSection id={id ?? undefined} section="facets">
          <AtomicPager ref={pagerRef} numberOfPages={numberOfPages} />
        </AtomicLayoutSection>
      </ClientOnly>
    </div>
  );
};
